///
/// EarthBrowser
///
/// Created by Matt Giger on 9/4/2026
/// Copyright (c) 2026 Geodata Labs
///

import { cTimeControlHeightVar, useChromeInsets } from "@/hooks/useBottomChromeOffset"
import { hapticTick } from "@/native/haptics"
import { sendCameraCommand, setAnimatedCameraTilt } from "@/redux/earthBrowserSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { userLocationService } from "@/services/userLocationService"
import { cn } from "@/utils/utils"
import { trig } from "@stovokor/math/math"
import { LocateFixedIcon, LocateIcon, LocateOffIcon, MinusIcon, NavigationIcon, Orbit, PlusIcon } from "lucide-react"
import { CompassControl } from "./CompassControl"

const TILT_3D_DEGREES = 45

/**
 * CameraControls - vertical camera stack in the bottom-right corner.
 *
 * Purely view manipulation: compass (click to face north / flatten tilt),
 * 2D/3D tilt toggle, orbit, my location and zoom in/out. Saving and
 * sharing the view live in ViewActions at the top-right since they
 * capture app state, not camera.
 *
 * My location cycles like the phone maps apps: first tap starts tracking
 * (blue marker) and flies the camera to follow the fix; on a device with
 * a compass the next tap turns the view with the heading; the next tap
 * stops tracking. A gesture that moves the camera drops back to "marker
 * only", from where a tap re-centers. The flight, following and the
 * marker are in the engine (camera-director/FollowBehavior.ts,
 * EarthActivity/utils/UserLocationMarker.ts); this button only drives
 * the location service and sends "location/*" camera commands.
 *
 * Sits level with the time control on desktop (both take their bottom
 * clearance from useChromeInsets). Stays pinned to the right edge even
 * with the data dock open: the dock's rail hugs the top of that edge and
 * caps its own height to leave this corner free (RightDock), and the
 * dock panel sits left of the rail. On phones the time control spans the
 * full width, so the stack rides on top of it using the height the bar
 * publishes.
 */
export function CameraControls() {
	const dispatch = useAppDispatch()
	const cameraTilt = useAppSelector(state => state.earthbrowser.cameraState.tilt)
	const cameraMode = useAppSelector(state => state.dialog.cameraMode)
	const infoSheetOpen = useAppSelector(state => state.dialog.mobileInfoSheetOpen)
	const location = useAppSelector(state => state.dialog.userLocation)
	const insets = useChromeInsets()
	const is3D = cameraTilt > 0.01
	const orbiting = cameraMode === "orbit"

	const tracking = location.status === "tracking" || location.status === "locating"
	const locating = location.status === "locating"
	const following = cameraMode === "follow"
	const headingMode = cameraMode === "heading"

	const handleTiltToggle = () => {
		hapticTick('light')
		dispatch(setAnimatedCameraTilt(is3D ? 0 : TILT_3D_DEGREES * trig.rad))
	}

	const stopLocation = () => {
		userLocationService.stop()
		dispatch(sendCameraCommand({ command: "location/stop" }))
	}

	const handleMyLocation = () => {
		hapticTick('light')
		if (!tracking) {
			// Permission prompts (location, and the compass on iOS) must
			// come from this gesture; the follow starts once a fix lands
			userLocationService.start(dispatch)
			dispatch(sendCameraCommand({ command: "location/follow" }))
		} else if (following) {
			if (location.headingSupported) {
				dispatch(sendCameraCommand({ command: "location/heading" }))
			} else {
				stopLocation()
			}
		} else if (headingMode) {
			stopLocation()
		} else {
			// Marker shown, camera free (the user panned away): re-center
			dispatch(sendCameraCommand({ command: "location/follow" }))
		}
	}

	const locationTitle = (() => {
		switch (location.status) {
			case "locating": return "Finding your location…"
			case "denied": return "Location access was denied. Allow it in your browser or system settings, then try again"
			case "unavailable": return "Your location is not available right now. Tap to try again"
			case "tracking":
				if (following) return location.headingSupported ? "Turn the view with your heading" : "Stop showing my location"
				if (headingMode) return "Stop showing my location"
				return "Re-center on my location"
			default: return "Show my location"
		}
	})()

	const LocationIcon = location.status === "denied" || location.status === "unavailable"
		? LocateOffIcon
		: headingMode
			? NavigationIcon
			: tracking
				? LocateFixedIcon
				: LocateIcon

	const buttonClass = cn(
		"flex items-center justify-center w-9 h-9 rounded-lg",
		"text-white/70 hover:text-white hover:bg-white/10 active:bg-white/20",
		"transition-colors duration-200",
		"focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
	)

	return (
		<div
			className={cn(
				"fixed right-2 z-30",
				"flex flex-col items-center gap-0 p-0.5",
				"bg-black/40 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl",
				"opacity-85 hover:opacity-95 transition-all duration-300",
				// Phones: the focused-entity info sheet docks over the bottom
				// edge; slide away with the time control
				infoSheetOpen && "translate-y-[calc(100%+32px)] opacity-0 pointer-events-none"
			)}
			style={{
				// Desktop: level with the time control. Phones (where the bar
				// is full width): 8px above it; the var is 0 when the bar is
				// hidden, which lands the stack back at the base offset
				bottom: `calc(${insets.bottom}px + var(--eb-camera-stack-lift, 0px))`,
			}}
			aria-hidden={infoSheetOpen || undefined}
			data-camera-controls
		>
			<CompassControl size={36} />

			<button
				onClick={handleTiltToggle}
				className={cn(
					buttonClass,
					"text-[11px] font-bold",
					is3D && "bg-purple-500/20 text-purple-300 hover:text-purple-200"
				)}
				title={is3D ? "Switch to top-down view" : "Switch to tilted 3D view"}
				aria-label="Toggle 2D/3D view"
			>
				{is3D ? "2D" : "3D"}
			</button>

			<button
				onClick={() => dispatch(sendCameraCommand({ command: "orbit/toggle" }))}
				className={cn(
					buttonClass,
					orbiting && "bg-cyan-500/20 text-cyan-300 hover:text-cyan-200"
				)}
				title={orbiting ? "Stop orbiting" : "Slowly orbit the current view"}
				aria-label="Toggle camera orbit"
				aria-pressed={orbiting}
			>
				<Orbit className={cn("w-4 h-4", orbiting && "animate-[spin_6s_linear_infinite]")} />
			</button>

			<button
				onClick={handleMyLocation}
				className={cn(
					buttonClass,
					locating && "text-cyan-300",
					(following || headingMode) && "bg-cyan-500/20 text-cyan-300 hover:text-cyan-200"
				)}
				title={locationTitle}
				aria-label="My location"
				aria-pressed={following || headingMode}
				data-location-status={location.status}
				data-location-mode={headingMode ? "heading" : following ? "follow" : tracking ? "marker" : "off"}
			>
				<LocationIcon className={cn("w-4 h-4", locating && "animate-pulse", headingMode && "fill-current")} />
			</button>

			<div className="w-6 h-px bg-white/20" />

			<button
				onClick={() => dispatch(sendCameraCommand({ command: "zoom/in" }))}
				className={buttonClass}
				title="Zoom in (+)"
				aria-label="Zoom in"
			>
				<PlusIcon className="w-4 h-4 stroke-[2.5]" />
			</button>

			<button
				onClick={() => dispatch(sendCameraCommand({ command: "zoom/out" }))}
				className={buttonClass}
				title="Zoom out (-)"
				aria-label="Zoom out"
			>
				<MinusIcon className="w-4 h-4 stroke-[2.5]" />
			</button>

			{/* Phone lift: the time control's published height plus a gap,
			    applied only below the sm breakpoint via a scoped style so the
			    inline calc above needs no JS media query */}
			<style>{`
				@media (max-width: 639.98px) {
					[data-camera-controls] {
						--eb-camera-stack-lift: calc(var(${cTimeControlHeightVar}, 0px) + 8px);
					}
				}
			`}</style>
		</div>
	)
}
